'use client';

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGetAllNews } from '@/services/newsHome/api';

const FALLBACK_DATA = {
    title: "VỀ CHÚNG TÔI",
    description: "Rovi Holdings là công ty đầu tư, thương mại và dịch vụ đa ngành nghề",
    content: `
        <p>Với mong muốn mang đến cho quý khách hàng, đối tác, nhà đầu tư và xã hội những sản phẩm – dịch vụ chất lượng, mới mẻ nên mọi lĩnh vực, Rovi Holdings đều chứng tỏ được vai trò dẫn đầu, trách nhiệm và hiệu quả của mình.</p>
        <p>Hệ sinh thái Vàng của Rovi Holdings tập trung ở 2 mảng lõi là du lịch và giải pháp logistics. Trong đó, bao gồm 5 lĩnh vực hoạt động: Du lịch - khách sạn, giải pháp logistics, công nghệ và tài chính.</p>
        <p>Không dừng lại ở nỗ lực mang lại giá trị cho khách hàng, Rovi Holdings luôn kỳ vọng sẽ hoàn thành trọn vẹn sứ mệnh, tạo ra giá trị gia tăng cho tất cả các thành phần tham gia vào chuỗi giá trị của tập đoàn và khẳng định tâm nhìn thương hiệu Việt trong quá trình hội nhập.</p>
    `
};

const ABOUT_ID = Number(process.env.NEXT_PUBLIC_ABOUT_US_NEWS_TYPE_ID);

const AboutUsSection = () => {

    /* 2. Gọi API lấy danh sách bài viết */
    const { data: newsData } = useQuery({
        queryKey: ['about-us-home-2101'],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: ABOUT_ID,
                isPublic: '',
                page: 1,
                pageSize: 50,
            });
            return res?.isOK ? res.result.items : [];
        },
        staleTime: 5 * 60 * 1000,
    });

    /* 3. Xử lý dữ liệu: Ưu tiên API (lọc theo seoDescription="Văn bản"), nếu không có thì dùng Fallback */
    const finalData = useMemo(() => {
        let selectedItem = null;
        if (newsData && Array.isArray(newsData)) {
            selectedItem = newsData.find((item: any) =>
                item.seoDescription?.toLowerCase().includes('văn bản')
            );
        }

        return selectedItem ? {
            title: selectedItem.title,
            description: selectedItem.description || "",
            content: selectedItem.content
        } : FALLBACK_DATA;

    }, [newsData]);

    /* 4. Hàm xử lý tô màu chữ "Rovi Holdings" */
    const renderHighlightedText = (text?: string) => {
        if (!text) return null;
        const keyPhrase = "Rovi Holdings";
        const parts = text.split(new RegExp(`(${keyPhrase})`, 'gi'));

        return (
            <>
                {parts.map((part, index) => (
                    part.toLowerCase() === keyPhrase.toLowerCase() ? (
                        <span key={index} className="text-[#006633] font-semibold">
                            {part}
                        </span>
                    ) : (
                        <span key={index} className="text-[#333333] font-normal">
                            {part}
                        </span>
                    )
                ))}
            </>
        );
    };

    return (
        <section className="w-full bg-white py-4 md:py-6 xl:py-12 2xl:py-17 min-[1920px]:py-23">
            <div className="max-w-[58rem] 2xl:max-w-[72rem] min-[1920px]:max-w-[96rem] mx-auto px-6 md:px-8">

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 md:gap-8 xl:gap-16 2xl:gap-24 min-[1920px]:gap-32 items-start">

                    {/* CỘT TRÁI: TIÊU ĐỀ */}
                    <div>
                        <p className="text-[#006633] text-[11px] xl:text-[12px] 2xl:text-sm min-[1920px]:text-base font-light tracking-[0.2em] uppercase mb-2 2xl:mb-4"
                            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                            {finalData.title}
                        </p>

                        <h2
                            className="text-2xl md:text-3xl xl:text-3xl 2xl:text-4xl min-[1920px]:text-5xl leading-tight"
                            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                        >
                            {renderHighlightedText(finalData.description)}
                        </h2>
                    </div>

                    {/* CỘT PHẢI: NỘI DUNG */}
                    <div
                        className="about prose w-full break-words whitespace-normal space-y-3 leading-snug pt-2 lg:max-w-lg text-[13px] md:text-sm text-[#333333]"
                        style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                        dangerouslySetInnerHTML={{ __html: finalData.content || '' }}
                    />
                </div>
            </div>
        </section>
    );
};

export default AboutUsSection;